import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Paper,
  Collapse,
} from "@mui/material";
import ScienceIcon from "@mui/icons-material/Science";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import GlassCard from "../../../components/common/GlassCard";

function LabsCard({ labs, onAddLab, onOpenLookup, onDeleteLab }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeLabId, setActiveLabId] = useState(null);
  const [expandAll, setExpandAll] = useState(false);

  const handleMenuOpen = (event, id) => {
    setMenuAnchor(event.currentTarget);
    setActiveLabId(id);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setActiveLabId(null);
  };

  const handleDelete = (labId) => {
    handleMenuClose();
    if (onDeleteLab) {
      onDeleteLab(labId);
    }
  };

  const sortedLabs = labs
    ? [...labs].sort((a, b) => {
        const dateA = new Date(a.collected_at || 0);
        const dateB = new Date(b.collected_at || 0);
        return dateB - dateA;
      })
    : [];

  return (
    <GlassCard 
      title="Lab Results" 
      icon={<ScienceIcon color="info" />} 
      onAdd={onAddLab}
      addLabel="Add Lab"
      onLookup={onOpenLookup}
      lookupLabel="Lab Lookup"
    >
      {sortedLabs?.length ? (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
            {sortedLabs.slice(0, 2).map((lab) => (
              <Paper
                key={lab.id}
                sx={{
                  p: 1.25,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.75}>
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight={600} mb={0.5}>
                      {lab.name} • {lab.collected_at ? new Date(lab.collected_at).toLocaleDateString() : "N/A"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {lab.value} {lab.unit || ""}
                    </Typography>
                  </Box>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, lab.id)}
                      sx={{ ml: 0.5 }}
                      aria-label="Lab options menu"
                      data-action="lab-menu"
                      data-lab-id={lab.id}
                    >
                      <MoreVertIcon aria-label="Lab options menu" fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>
            ))}
          </Box>

          {sortedLabs?.length > 2 && (
            <>
              <Collapse in={expandAll} timeout="auto" unmountOnExit>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
                  {sortedLabs.slice(2).map((lab) => (
                    <Paper
                      key={lab.id}
                      sx={{
                        p: 1.25,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.75}>
                        <Box flex={1}>
                          <Typography variant="body2" fontWeight={600} mb={0.5}>
                            {lab.name} • {lab.collected_at ? new Date(lab.collected_at).toLocaleDateString() : "N/A"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {lab.value} {lab.unit || ""}
                          </Typography>
                        </Box>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, lab.id)}
                            sx={{ ml: 0.5 }}
                            aria-label="Lab options menu"
                            data-action="lab-menu"
                            data-lab-id={lab.id}
                          >
                            <MoreVertIcon aria-label="Lab options menu" fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </Collapse>

              <Button
                size="small"
                onClick={() => setExpandAll(!expandAll)}
                startIcon={expandAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ textTransform: "none", mt: 1 }}
                aria-label={expandAll ? "Collapse lab results list" : "Expand lab results list"}
                data-action="toggle-labs-expansion"
              >
                {expandAll ? "View Less" : `View All (${sortedLabs.length - 2} more)`}
              </Button>
            </>
          )}
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No lab results
        </Typography>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            if (activeLabId) {
              onDeleteLab(activeLabId);
            }
          }}
          aria-label="Delete lab result"
          data-action="delete-lab"
          data-lab-id={activeLabId}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>
    </GlassCard>
  );
}

LabsCard.propTypes = {
  labs: PropTypes.array,
  onAddLab: PropTypes.func.isRequired,
  onOpenLookup: PropTypes.func.isRequired,
  onDeleteLab: PropTypes.func.isRequired,
};

LabsCard.defaultProps = {
  labs: [],
};

export default LabsCard;
